import { createSelector } from '@ngrx/store';
import { docsFeature } from '../docs/docs.reducer';
import { docKey } from '../docs/docs.normalize';
import { selectActiveProjectId } from '../workspace/workspace.selectors';
import { workflowFeature } from './workflow.reducer';
import {
  DbDesignSummary,
  DefineSummary,
  TicketsSummary,
  WorkflowSectionStatus,
  computeDbDesignSummary,
  computeDefineSummary,
  computeStatuses,
  computeTicketsSummary,
} from './workflow.models';

const { selectEntities, selectKeyIndex } = docsFeature;
const { selectBoard } = workflowFeature;

function dataFor(
  entities: ReturnType<typeof selectEntities>,
  keyIndex: Record<string, string>,
  pid: string | null,
  key: string
): any {
  if (!pid) return null;
  const id = keyIndex[docKey(pid, key)];
  return id ? entities.entities[id]?.data ?? null : null;
}

const selectWorkflowDefineData = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): any => dataFor(e, k, pid, 'define.definition')
);

const selectWorkflowDbDesignData = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): any => dataFor(e, k, pid, 'db-design.main')
);

const selectWorkflowMarketplaceSeed = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): any => dataFor(e, k, pid, 'marketplace.state')
);

// Doc-shaped wrapper for consumers that read `.data` off the define doc.
export const selectWorkflowDefineDoc = createSelector(
  selectWorkflowDefineData,
  (data): { data: any } | null => (data == null ? null : { data })
);

export const selectWorkflowDefineSummary = createSelector(
  selectWorkflowDefineData,
  (data): DefineSummary => computeDefineSummary(data)
);

export const selectWorkflowDbDesignSummary = createSelector(
  selectWorkflowDbDesignData,
  (data): DbDesignSummary => computeDbDesignSummary(data)
);

export const selectWorkflowTicketsSummary = createSelector(
  selectBoard,
  (board): TicketsSummary => computeTicketsSummary(board)
);

export const selectWorkflowStatuses = createSelector(
  selectActiveProjectId,
  selectWorkflowDefineData,
  selectWorkflowDbDesignData,
  selectWorkflowMarketplaceSeed,
  selectBoard,
  (projectId, defineData, dbDesignMainData, marketplaceSeed, board): WorkflowSectionStatus[] =>
    computeStatuses({ projectId, defineData, dbDesignMainData, marketplaceSeed, board })
);

export const selectWorkflowIssues = createSelector(selectWorkflowStatuses, (statuses) =>
  statuses.flatMap((s) => s.issues.map((i) => `${s.title}: ${i}`))
);
